import { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowRight, Menu, X } from 'lucide-react';
import { EMAIL, useLanguage } from '../i18n/content';
import { smooth } from '../lib/smooth';
import { LangSwitch } from './LangSwitch';
import { EASE } from './Reveal';

const SECTION_IDS = ['beranda', 'tentang', 'layanan', 'kontak'];

export function Header() {
  const { t } = useLanguage();
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState('beranda');

  useEffect(() => {
    let raf;
    const onScroll = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        const pos = window.scrollY + 140;
        let cur = 'beranda';
        for (const id of SECTION_IDS) {
          const el = document.getElementById(id);
          if (el && el.offsetTop <= pos) cur = id;
        }
        setActive(cur);
      });
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => {
      window.removeEventListener('scroll', onScroll);
      cancelAnimationFrame(raf);
    };
  }, []);

  useEffect(() => {
    if (open) {
      smooth.lenis?.stop();
      document.body.style.overflow = 'hidden';
    } else {
      smooth.lenis?.start();
      document.body.style.overflow = '';
    }
    return () => {
      smooth.lenis?.start();
      document.body.style.overflow = '';
    };
  }, [open]);

  const items = SECTION_IDS.map((id) => ({ id, label: t.nav[id] }));

  return (
    <>
      <header className="fixed inset-x-0 top-0 z-50 border-b border-white/5 bg-[#0A0A0C]/80 backdrop-blur-md">
        <div className="mx-auto flex h-20 max-w-7xl items-center justify-between gap-4 px-4 sm:h-24 sm:px-8 lg:h-28 lg:px-8">
          <a
            href="#beranda"
            data-testid="header-logo"
            aria-label="NOFINA"
            className="shrink-0"
          >
            <img
              src="/nofina-logo.webp"
              alt="NOFINA"
              width="2000"
              height="667"
              className="h-auto w-[180px] sm:w-[220px] lg:w-[280px]"
            />
          </a>

          <nav className="hidden lg:flex items-center gap-6">
            {items.map(({ id, label }) => (
              <a
                key={id}
                href={`#${id}`}
                data-testid={`header-nav-${id}`}
                className={`relative pb-1 text-sm font-medium transition-colors duration-300 ${
                  active === id ? 'text-white' : 'text-slate-300 hover:text-white'
                }`}
              >
                {label}
                <span
                  className={`absolute -bottom-0.5 left-0 h-[2px] w-full rounded-full bg-nofina-red transition-transform duration-300 ${
                    active === id ? 'scale-x-100' : 'scale-x-0'
                  }`}
                />
              </a>
            ))}
          </nav>

          <div className="flex items-center gap-4 sm:gap-5">
            <LangSwitch testIdPrefix="header-lang" />
            <a
              href={`mailto:${EMAIL}`}
              data-testid="header-contact-button"
              className="hidden lg:inline-flex items-center gap-2 rounded-xl bg-nofina-red px-5 py-2.5 text-sm font-semibold text-white transition-all duration-300 hover:bg-nofina-redhover hover:shadow-[0_0_30px_rgba(229,9,20,0.45)]"
            >
              {t.ctaContact}
              <ArrowRight className="h-4 w-4" />
            </a>
            <button
              type="button"
              data-testid="mobile-menu-button"
              aria-label={open ? 'Tutup menu' : 'Buka menu'}
              aria-expanded={open}
              onClick={() => setOpen((v) => !v)}
              className="flex h-11 w-11 items-center justify-center rounded-xl border border-white/10 text-white lg:hidden"
            >
              {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </button>
          </div>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="mobile-menu"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 top-20 z-40 bg-[#0A0A0C] sm:top-24 lg:hidden"
            data-lenis-prevent
          >
            <div className="flex h-full flex-col overflow-y-auto px-6 pb-10 pt-6">
              <nav className="flex flex-col">
                {items.map(({ id, label }, i) => (
                  <motion.a
                    key={id}
                    href={`#${id}`}
                    data-testid={`mobile-nav-${id}`}
                    onClick={() => setOpen(false)}
                    initial={{ opacity: 0, x: -16 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.06 + i * 0.06, duration: 0.5, ease: EASE }}
                    className="group flex items-center justify-between border-b border-white/5 py-5"
                  >
                    <span className="font-display text-2xl font-bold text-white transition-colors group-hover:text-nofina-red">
                      {label}
                    </span>
                    <span className="font-mono text-xs tracking-widest text-nofina-red">
                      0{i + 1}
                    </span>
                  </motion.a>
                ))}
              </nav>
              <div className="mt-auto flex items-center justify-between gap-4 pt-10">
                <LangSwitch testIdPrefix="mobile-lang" />
                <a
                  href={`mailto:${EMAIL}`}
                  data-testid="mobile-contact-button"
                  onClick={() => setOpen(false)}
                  className="inline-flex items-center gap-2 rounded-xl bg-nofina-red px-5 py-3 text-sm font-semibold text-white"
                >
                  {t.ctaContact}
                  <ArrowRight className="h-4 w-4" />
                </a>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
